import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import i18n from "i18next";
import type { TFunction } from "i18next";
import { fetchUserData } from "../../Models/firebaseHelper";
import { auth } from "../../Models/firebaseConfig";

interface UserContextType {
  user: UserData | null;
  userLoading: boolean;
  userError: string | null;
  updateUser: () => Promise<void>;
  refreshUser: () => Promise<void>;
  clearUserError: () => void;
}

const initialUser: UserContextType = {
  user: null,
  userLoading: true,
  userError: null,
  updateUser: () => {
    return Promise.resolve();
  },
  refreshUser: () => {
    return Promise.resolve();
  },
  clearUserError: () => {},
};

export const UserContext = createContext<UserContextType>(initialUser);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserData | null>(null);
  const [userLoading, setUserLoading] = useState(true);
  const [userError, setUserError] = useState<string | null>(null);

  const getUserData = useCallback(async () => {
    try {
      setUserError(null);
      const data = await fetchUserData();
      setUser(data);
    } catch (error: any) {
      console.error("Failed to fetch user data:", error);
      setUserError(getUserErrorMessage(error, i18n.t.bind(i18n)));
    } finally {
      setUserLoading(false);
    }
  }, []);

  const updateUser = useCallback(async () => {
    setUserLoading(true);
    await getUserData();
  }, [getUserData]);

  const refreshUser = useCallback(async () => {
    try {
      const data = await fetchUserData();
      setUser(data);
      setUserError(null);
    } catch (error: any) {
      console.error("Failed to silently refresh user data:", error);
    }
  }, [setUser, setUserError]);

  const clearUserError = useCallback(() => {
    setUserError(null);
  }, [setUserError]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        getUserData();
      } else {
        // User logged out - clear all cache and localStorage to prevent data leakage
        localStorage.clear();
        setUser(null);
        setUserError(null);
        setUserLoading(false);
      }
    });

    return unsubscribe;
  }, [getUserData]);

  return (
    <UserContext.Provider
      value={{
        user,
        userLoading,
        userError,
        updateUser,
        refreshUser,
        clearUserError,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

/**
 * Converts Firebase error to user-friendly message
 */
function getUserErrorMessage(error: any, t: TFunction): string {
  if (error?.code === "functions/internal") {
    return t("userErrors.serverError");
  } else if (error?.code === "functions/unauthenticated") {
    return t("userErrors.loginRequired");
  } else if (error?.code === "functions/permission-denied") {
    return t("userErrors.permissionDenied");
  } else if (error?.code === "functions/unavailable") {
    return t("userErrors.serviceUnavailable");
  } else if (error?.message?.includes("INTERNAL")) {
    return t("userErrors.serverError");
  } else if (error?.message?.includes("Failed to fetch")) {
    return t("userErrors.checkConnection");
  } else {
    return t("userErrors.unexpectedError");
  }
}

export const useUser = () => useContext(UserContext);
